/**
 * One-shot messages from user gestures to the playback schedule.
 *
 * The schedule can see THAT the playhead jumped, but not WHY. That distinction matters twice
 * over: a click's `setPlayingFragment(null)` is indistinguishable from one of the schedule's own
 * writes still in flight (waiting on it made clicked voicings join late), and a cue entered
 * mid-window by a deliberate reposition should start mid-sentence at the matching words, not
 * restart from its first word against a lecturer who is half-way through the line.
 *
 * Module state, not React state: these travel from a click handler to a 25ms scheduler tick and
 * from the scheduler to a media element, and a render cycle in that path is exactly the latency
 * being engineered out.
 */

let seekAnnouncedAt = 0;

/** A user gesture just moved the playhead (wave click, subtitle click, shortcut). */
export const announceUserSeek = (): void => {
    seekAnnouncedAt = Date.now();
};

/** True once per announcement, and only while it is fresh enough to still mean anything. */
export const consumeUserSeek = (): boolean => {
    if (!seekAnnouncedAt) return false;
    const fresh = Date.now() - seekAnnouncedAt < 2000;
    seekAnnouncedAt = 0;
    return fresh;
};

const alignedStarts = new Map<string, number>();

/** The next time this cue's turn comes, it should start this far into its clip. */
export const requestAlignedStart = (id: string, offsetMs: number): void => {
    alignedStarts.set(id, offsetMs);
};

/** The requested offset for this cue, one-shot; null means a normal from-the-top start. */
export const consumeAlignedStart = (id: string): number | null => {
    const offset = alignedStarts.get(id);
    if (offset === undefined) return null;
    alignedStarts.delete(id);
    return offset;
};

/**
 * Drop every pending offset.
 *
 * An aligned start is a one-shot instruction for the NEXT turn of one cue. If that turn never
 * comes — playback stopped, the folder changed — the instruction survives in the map and hijacks
 * an ordinary playthrough of that cue much later, which sounds like a voicing starting mid-word
 * for no reason.
 */
export const clearAlignedStarts = (): void => {
    alignedStarts.clear();
};
